import { iconClassName } from '../../../helpers';

/**
 * Whether graph nodes carry their dataset type's icon.
 *
 * One switch for the whole thing: with this off nothing reads an icon URL and the graphs'
 * stylesheets leave the node background alone, which is the state they were in before.
 *
 * Off for now. The icons draw, and cytoscape centres them correctly, but the assets declare
 * width="100%" height="100%" over a square viewBox, so a browser rasterises them into its default
 * 300x150 box with the artwork centred inside — leaving the drawn image full height and half
 * width, which reads as badly placed however it is positioned. Turning this on again is a matter
 * of the assets carrying an intrinsic size; see the discussion on issue #91.
 */
export const SHOW_DATASET_TYPE_ICON_IN_NODES = false;

/**
 * Resolved URLs, keyed by theme and class. Reading a computed style forces layout, so each icon is
 * looked up once; the theme is part of the key because a stylesheet may point a class at a
 * different file in dark mode (the "external link" icon already does).
 */
const cache = new Map();

/**
 * The image URL behind an icon CSS class, read out of the stylesheet that declares it.
 *
 * Icon.css is the one place that maps an icon to a file, and the filenames are not derivable —
 * `.icon-semantic-model` points at `cube.svg`. Reading the declaration back rather than repeating it
 * here keeps that mapping in one place: a new dataset type needs only its CSS rule, and a future
 * dark-mode variant is picked up without a change here. Mirrors how the graph stylesheet already
 * reads its colours out of the CSS custom properties.
 *
 * @param {string} className - The icon class to resolve, e.g. 'icon-semantic-model'.
 * @returns {string|undefined} The image URL, or undefined when the class declares no background.
 */
const _iconUrl = className => {
  const theme = document.documentElement.getAttribute('data-theme') || 'system';
  const key = `${theme}|${className}`;

  if (cache.has(key)) return cache.get(key);

  const probe = document.createElement('div');

  probe.className = `icon ${className}`;
  // Out of the flow and out of sight, but still styled: display:none would give an empty background.
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;';
  document.body.appendChild(probe);

  const background = window.getComputedStyle(probe).backgroundImage;

  document.body.removeChild(probe);

  // Computed form is url("…"); anything else (none, a gradient) means this class has no image.
  const url = /^url\((['"]?)(.*)\1\)$/.exec(background)?.[2];

  cache.set(key, url);

  return url;
};

/**
 * The icon URL for a dataset type, for drawing inside a graph node.
 *
 * @param {string} [datasetTypeName] - The dataset's dataset_type_name, e.g. 'Semantic Model'.
 * @returns {string|undefined} The icon's image URL, or undefined when there is none to draw.
 */
export const datasetTypeIconUrl = datasetTypeName => {
  if (!SHOW_DATASET_TYPE_ICON_IN_NODES || !datasetTypeName) return undefined;

  return _iconUrl(`icon-${iconClassName(datasetTypeName)}`);
};
